<script setup>
import { nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

import { DEV_MODE } from '@/config/dev-mode';
import { CONTENTS as YONGSIN_CONTENTS, CONTENTS_PAGE_MAP as YONGSIN_SECTION_PAGE_MAP } from './constants';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi.js';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { addContentLogAPI } from '@/api/logServiceApi';

import youngsinGuide01 from './GuideYongsin01.vue';
import SajuInsightYongsinContents01 from './ContentsYongsin01.vue';
import SajuInsightYongsinContents02 from './ContentsYongsin02.vue';
import SajuInsightYongsinContents03 from './ContentsYongsin03.vue';
import SajuInsightYongsinContents04 from './ContentsYongsin04.vue';
import SajuInsightYongsinContents05 from './ContentsYongsin05.vue';
import SajuInsightYongsinContents06 from './ContentsYongsin06.vue';
import SajuInsightYongsinContents07 from './ContentsYongsin07.vue';
import SajuInsightYongsinContents08 from './ContentsYongsin08.vue';
import SajuInsightYongsinContents09 from './ContentsYongsin09.vue';
import ContentsList from '@/components/saju-insight/common/ContentsList.vue';

import { isObjectEmpty } from '@/composables/functions';

const props = defineProps({
  updateSelectedDescNav: {
    type: Function,
  },
  hasPermission: {
    type: Boolean,
  },
});
const emit = defineEmits(['goPopAnalysis']);

const route = useRoute();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();

const { sajuData } = storeToRefs(dataStore);

// =============== 페이지 ===============
/** 페이지 이동 (로그 없이) */
const moveToPage = (id) => {
  if (id < 1) {
    props.updateSelectedDescNav('');
  } else {
    if (id > 1 && !props.hasPermission) {
      emit('goPopAnalysis');
      return;
    }
    props.updateSelectedDescNav('youngsinJob');
  }
  indexNo.value = id;
};

/** index값으로 페이지 이동 (목차 클릭 또는 페이지네이션) */
const updateIndexNo = (id, tocIdx) => {
  if (tocIdx != null) {
    // 목차 리스트 클릭
    addContentLogAPI({
      contentType: props.hasPermission ? 'ay_yongsin_toc_paid_opt3' : 'ay_yongsin_toc_free_opt3',
      option3: String(tocIdx),
    });
  } else {
    // 이전/다음 화살표 클릭
    addContentLogAPI({
      contentType: 'ay_yongsin_page_opt3',
      option3: String(id),
    });
  }
  moveToPage(id);
};

// 인덱스
const indexNo = ref(1);
watch(indexNo, (newIndex) => {
  store.setYongsinIndex(newIndex);
});
onUnmounted(() => {
  store.setYongsinIndex(1);
});

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const target = YONGSIN_SECTION_PAGE_MAP[newIdx];
    if (target) moveToPage(target);
  },
  { immediate: true },
);

/** 표지로 이동 */
const goToOverView = () => {
  store.clickMainMenu('/overviewYongsin');
};

// =============== 용신분석 데이터 및 상태 ===============
const guestId = route.params.id;

const isYongsinLoading = ref(true);

const yongsinParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestId,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 4,
  };
});

const isReadyToShow = computed(() => {
  return !isYongsinLoading.value && !isObjectEmpty(dataStore.yData);
});

/** 용신분석 데이터 초기화 */
const initYongsinData = async () => {
  if (!props.hasPermission) {
    await fetchSubData();
  } else {
    await fetchYongsinData();
  }
};

/** 용신분석 무료 데이터 요청 */
const fetchSubData = async () => {
  isYongsinLoading.value = true;
  try {
    if (isObjectEmpty(dataStore.pSubData)) {
      const subDataParams = {
        ...yongsinParams.value,
        analysisTypeId: 8,
      };

      const result = await fetchBaseServiceAPI(subDataParams);

      dataStore.setPSubData(result?.data?.성향종합분석);
    }
  } catch (e) {
  } finally {
    isYongsinLoading.value = false;
  }
};

/** 내 용신분석 데이터 요청 */
const fetchYongsinData = async () => {
  isYongsinLoading.value = true;
  try {
    if (isObjectEmpty(dataStore.yData)) {
      const result = await fetchBaseServiceAPI(yongsinParams.value);

      dataStore.setYData(result?.data);
      store.setAnalysisResult(result?.data);
    }
  } catch (error) {
    DEV_MODE && console.error('/youngsin.vue fetchYongsinData 실패:', error);
  } finally {
    isYongsinLoading.value = false;
  }
};

watch(
  sajuData,
  async (newData, oldData) => {
    if (!isObjectEmpty(newData)) {
      await initYongsinData();
    }
  },
  { immediate: true },
);

watch(
  () => props.hasPermission,
  async (newValue, oldValue) => {
    if (!isObjectEmpty(sajuData.value) && !oldValue && newValue) {
      await initYongsinData();
    }
  },
);

// =============== 평가하기 데이터 ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

/** 평가조회 */
const getSajuYongsinRate = async () => {
  if (!props.hasPermission) return;

  try {
    const params = {
      serviceName: 'YONGSIN_ANALYSIS',
      objectKey: `${guestId}`,
    };

    const result = await getDailyFortuneRateAPI(params);

    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

/** 평가하기 */
const postSajuYongsinRate = async (score) => {
  try {
    const params = {
      score,
      serviceName: 'YONGSIN_ANALYSIS',
      objectKey: `${guestId}`,
    };

    const result = await postDailyFortuneRateAPI(params);

    if (result) {
      ratingStatus.value = true;
      getSajuYongsinRate();
    }
  } catch (error) {}
};

onMounted(() => {
  getSajuYongsinRate();
});
</script>

<template>
  <div v-if="store.selectedMenu.endsWith('youngsin')" class="full-height">
    <div v-if="isYongsinLoading" class="analysis__loading-overlay full-height">
      <div class="loading-olverlay__img"></div>
      용신 분석중입니다!
    </div>
    <youngsinGuide01 v-if="indexNo === 1" class="full-height" />
    <SajuInsightYongsinContents01
      v-if="indexNo === 2 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
      :pSubData="dataStore.pSubData"
      :userName="store.userName"
      :birthData="sajuData.birthganji"
      :birthJijanggan="sajuData.jijanggan"
      :langMode="sajuData.lang || 'CHNKOR'"
      :birthTitle="sajuData.transdata.title || '시일월년'"
      :transdata="sajuData.transdata"
      :sajuData="sajuData"
      :isActive="sajuData.transdata.isActive"
    />
    <SajuInsightYongsinContents02
      v-if="indexNo === 3 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents03
      v-if="indexNo === 4"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents04
      v-if="indexNo === 5 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents05
      v-if="indexNo === 6 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents06
      v-if="indexNo === 7 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents08
      v-if="indexNo === 8 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents09
      v-if="indexNo === 9 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
    />
    <SajuInsightYongsinContents07
      v-if="indexNo === 10 && isReadyToShow"
      class="full-height"
      :yData="dataStore.yData"
      :ratingResult="ratingResult"
      :ratingStatus="ratingStatus"
      @postRate="postSajuYongsinRate"
      @goToOverView="goToOverView"
    />
  </div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="YONGSIN_CONTENTS"
      :curPage="indexNo"
      :hasPermission="hasPermission"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>
<style>
@import '/src/assets/nangman_style/personality.scss';
</style>
